<script lang="ts">
    import type {
        CountryCode,
        DetailedValue,
        E164Number,
    } from "svelte-tel-input/types";
    import type * as t from "./types";
    import type { SequenceNode } from "./sequence";

    import { fly } from "svelte/transition";
    import { TelInput, normalizedCountries } from "svelte-tel-input";

    import store from "./quiz-store";
    import { calculateResult, goToNextStep } from "./quiz-controller.svelte";
    import eventLogger from "./quiz-event-logger";
    import { getPrizeText } from "./utils";
    import { Klaviyo } from "./integrations/klaviyo";

    export let node: SequenceNode<t.Segment>;
    export let step: t.Step;

    const STORE_HANDLE = window.Shopify.shop;
    const GOOGLE_SCRIPT_URL =
        "https://script.google.com/macros/s/AKfycbzv_XTTbTeL0FSd4l8hGlEJTqBspUmDOeKeBqf5qmoTzunIq7hTieSTq24po1O01OiO/exec";
    const segment = node.value;
    const quizConfig = $store.config;
    const config = $store.config.segments[segment.id];
    const nextConfig =
        node.next != null ? $store.config.segments[node.next.value.id] : null;
    const prize = config.prize;
    const prizeText = getPrizeText(prize);
    const klaviyoConfig = $store.klaviyoConfig;

    let networkStatus: "idle" | "loading" = "idle";
    let isShowingForm = false;
    let selectedCountry: CountryCode | null = "US";
    let phoneInputValue: E164Number | null = null;
    let detailedPhoneInputValue: DetailedValue | null = null;
    let isPhoneInputValid = true;
    let phoneInputError: string | undefined;
    let formInputType: "phone" | "email" = step.defaultFormInputType;

    $: revalidatePhoneInput(isPhoneInputValid);

    function handleContinue() {
        eventLogger.logEvent("continue_playing", {
            level_id: segment.id,
            next_level_id: node.next?.value.id,
        });
        goToNextStep();
    }

    function handleClaimPrize() {
        isShowingForm = true;
    }

    function subscribeToKlaviyo(email?: string, phone?: string) {
        if (klaviyoConfig == null) {
            return;
        }

        const klaviyo = new Klaviyo(
            klaviyoConfig.publicApiKey,
            klaviyoConfig.listId,
        );

        klaviyo.subscribeClient({
            email,
            phone,
        });
    }

    function handleSubmitForm(e: SubmitEvent) {
        e.preventDefault();

        if (formInputType === "phone" && !isPhoneInputValid) {
            phoneInputError = "invalid phone number";
            return;
        }

        phoneInputError = undefined;
        networkStatus = "loading";

        const formData = new FormData(e.target as HTMLFormElement);
        // We don't care about the response. If it fails before the redirect,
        // at least we can log it.
        fetch(GOOGLE_SCRIPT_URL, {
            method: "POST",
            body: formData,
        }).catch((error) => {
            console.error("[shpln-quiz] Unable to save email", error);
        });

        if (klaviyoConfig != null) {
            const email = formData.get("email") as string | undefined;
            const phone = detailedPhoneInputValue?.phoneNumber ?? undefined;
            subscribeToKlaviyo(email, phone);
        }

        if (node.next != null) {
            eventLogger.logEvent("abandon_game", {
                level_id: segment.id,
            });
        }

        let discountPrizeInfo = {};
        if (prize.type === "discount") {
            discountPrizeInfo = {
                prize_discount_type: prize.discount_type,
                prize_discount_value: prize.discount_value,
            };
        }

        let customPrizeInfo = {};
        if (prize.type === "custom") {
            customPrizeInfo = {
                prize_custom_value: prize.custom_value,
            };
        }

        eventLogger.logEvent("claim_prize", {
            level_id: segment.id,
            prize_type: prize.type,
            ...discountPrizeInfo,
            ...customPrizeInfo,
        });

        const result = calculateResult(segment.id);

        if (result == null) {
            return;
        }

        let href: string;
        if (prize.type === "custom") {
            href = result.redirectUrl;
        } else {
            href = result.discountUrl;
        }

        window.location.href = href;
    }

    function revalidatePhoneInput(isValid: boolean) {
        if (phoneInputError == null) {
            return;
        }

        if (isValid) {
            phoneInputError = undefined;
        }
    }
</script>

<div
    class="flex w-full max-w-96 flex-col justify-center"
    class:pointer-events-none={networkStatus === "loading"}
>
    <div
        class="flex w-full flex-col items-stretch gap-8 {isShowingForm
            ? 'h-full'
            : ''}"
    >
        <div
            class="flex w-full {step.subtitle?.position === 'above_title'
                ? 'flex-col-reverse'
                : 'flex-col'} items-center gap-1"
        >
            <h1 class="text-center text-xl font-bold sm:text-2xl">
                {step.title}
            </h1>
            {#if step.subtitle != null}
                <h2 class="text-center text-sm leading-none">
                    {step.subtitle.text}
                </h2>
            {/if}
        </div>
        <div
            class="flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2"
        >
            {#if isShowingForm}
                <form
                    on:submit={handleSubmitForm}
                    class="flex w-full flex-col items-center gap-2"
                >
                    <input name="store" type="hidden" value={STORE_HANDLE} />

                    <div
                        class="flex w-full flex-1 flex-col items-stretch gap-2"
                    >
                        {#if formInputType === "email"}
                            <input
                                class="h-14 rounded px-3 text-center"
                                name="email"
                                type="email"
                                placeholder="Email"
                                required
                            />
                            <div class="flex justify-center">
                                <span>
                                    or <button
                                        type="button"
                                        class="text-[var(--primary-color)] underline"
                                        on:click={() => {
                                            formInputType = "phone";
                                        }}>continue with phone number</button
                                    >
                                </span>
                            </div>
                        {:else}
                            {#if phoneInputError != null}
                                <span class="text-[var(--error-color)]"
                                    >{phoneInputError}</span
                                >
                            {/if}
                            <div
                                class="phone-input-wrapper flex h-14 items-stretch rounded px-3 {phoneInputError !=
                                null
                                    ? 'invalid'
                                    : ''}"
                            >
                                <select
                                    class="country-select w-[30%] {!isPhoneInputValid
                                        ? 'invalid'
                                        : ''}"
                                    aria-label="Default select example"
                                    name="Country"
                                    bind:value={selectedCountry}
                                >
                                    <option
                                        value={null}
                                        hidden={selectedCountry !== null}
                                        >Please select</option
                                    >
                                    {#each normalizedCountries as currentCountry (currentCountry.id)}
                                        <option
                                            value={currentCountry.iso2}
                                            selected={currentCountry.iso2 ===
                                                selectedCountry}
                                            aria-selected={currentCountry.iso2 ===
                                                selectedCountry}
                                        >
                                            {currentCountry.iso2} (+{currentCountry.dialCode})
                                        </option>
                                    {/each}
                                </select>
                                <TelInput
                                    bind:country={selectedCountry}
                                    bind:value={phoneInputValue}
                                    bind:detailedValue={detailedPhoneInputValue}
                                    bind:valid={isPhoneInputValid}
                                    class="basic-tel-input flex-1"
                                    name="phone"
                                    required
                                />
                            </div>
                            <div class="flex justify-center">
                                <span>
                                    or <button
                                        type="button"
                                        class="text-[var(--primary-color)] underline"
                                        on:click={() => {
                                            formInputType = "email";
                                        }}>continue with email</button
                                    >
                                </span>
                            </div>
                        {/if}

                        <button
                            type="submit"
                            class="flex h-14 w-full flex-col items-center justify-center rounded p-2"
                            class:bg-none={networkStatus === "loading"}
                            class:bg-[var(--primary-color)]={networkStatus ===
                                "idle"}
                            disabled={networkStatus === "loading"}
                        >
                            {#if networkStatus === "idle"}
                                <div
                                    class="text-lg uppercase text-[var(--text-on-primary-color)] transition-transform hover:scale-105"
                                    transition:fly={{ y: 24 }}
                                >
                                    Get {prizeText}
                                </div>
                            {/if}
                            {#if networkStatus === "loading"}
                                <div
                                    class="flex flex-col items-center justify-center gap-4"
                                    transition:fly={{ y: 24 }}
                                >
                                    <div class="p-4">
                                        <div
                                            class="dot-typing"
                                            style="--dots-color:var(--secondary-color);"
                                        ></div>
                                    </div>
                                </div>
                            {/if}
                        </button>
                    </div>

                    {#if nextConfig != null}
                        <div class="flex flex-col items-center justify-center">
                            <span class="text-md">or</span>
                            <button
                                type="button"
                                on:click={handleContinue}
                                class="p-2 text-lg uppercase text-[var(--primary-color)] hover:underline"
                                >{step.continueButton.text}</button
                            >
                        </div>
                    {/if}
                </form>
            {:else}
                {#if nextConfig != null}
                    <button
                        on:click={handleContinue}
                        class="continue min-h-14 w-full rounded-lg px-3 uppercase transition-transform hover:scale-105"
                        >{step.continueButton.text}</button
                    >
                {/if}
                <button
                    on:click={handleClaimPrize}
                    class="claim h-14 w-full rounded-lg px-3 uppercase"
                >
                    Take the {prizeText}
                </button>
            {/if}
        </div>
    </div>
    {#if isShowingForm && quizConfig.privacyPolicyUrl != null}
        <div class="mt-4 text-center text-xs leading-none text-gray-300">
            We use your data to send updates and offers. See our <a
                class="font-bold underline"
                href={quizConfig.privacyPolicyUrl}>Privacy Policy</a
            >.
        </div>
    {/if}
</div>

<style lang="scss">
    button.claim {
        color: var(--primary-color);
        border-style: solid;
        border-width: 2px;
        border-color: var(--primary-color);
        background-color: whitesmoke;
    }

    button.continue {
        color: var(--text-on-primary-color);
        background-color: var(--primary-color);
    }

    form input,
    .phone-input-wrapper {
        border-style: solid;
        border-width: 2px;
        border-color: var(--primary-color);
        background-color: whitesmoke;
    }

    .phone-input-wrapper {
        :global(.basic-tel-input),
        :global(.country-select) {
            height: 100%;
            padding-left: 8px;
            background-color: whitesmoke;
        }

        :global(.country-select) {
            padding: 0;
        }

        :global(.basic-tel-input:focus),
        :global(.country-select:focus) {
            outline: none;
        }
    }

    .phone-input-wrapper.invalid {
        border-color: var(--error-color);
    }

    .dot-typing {
        position: relative;
        left: -9999px;
        width: 10px;
        height: 10px;
        border-radius: 5px;
        background-color: var(--dots-color);
        color: var(--dots-color);
        box-shadow:
            9984px 0 0 0 var(--dots-color),
            9999px 0 0 0 var(--dots-color),
            10014px 0 0 0 var(--dots-color);
        animation: dot-typing 1.5s infinite linear;
    }

    @keyframes dot-typing {
        0% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
        16.667% {
            box-shadow:
                9984px -10px 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
        33.333% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
        50% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px -10px 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
        66.667% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
        83.333% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px -10px 0 0 var(--dots-color);
        }
        100% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
    }
</style>
